import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { MessageSquare, Save, RefreshCcw, ThumbsUp } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FEEDBACK_TYPES = [
  ["summary", "Summary"],
  ["triage", "Triage"],
  ["root_cause", "Root cause"],
  ["agent_analysis", "Agent analysis"],
  ["consensus", "Consensus"],
  ["runbook", "Runbook"],
  ["business_impact", "Business impact"],
  ["other", "Other"],
];

const RATINGS = [
  ["useful", "Useful"],
  ["partially_useful", "Partially useful"],
  ["not_useful", "Not useful"],
];

export default function ReviewerFeedbackPanel({ incident, onUpdate, onRerun }) {
  const [feedbackType, setFeedbackType] = useState("root_cause");
  const [rating, setRating] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (alsoRerun = false) => {
    if (!comment.trim()) {
      toast.error("Add a feedback comment first");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.submitFeedback(incident.id, {
        feedback_type: feedbackType,
        rating: rating || null,
        comment: comment.trim(),
        reviewer: "On-call Reviewer",
      });
      onUpdate?.(updated);
      setComment("");
      setRating("");
      toast.success("Feedback saved to audit transcript");
      if (alsoRerun) {
        await onRerun?.();
      }
    } catch (e) {
      toast.error("Could not save feedback");
    } finally {
      setBusy(false);
    }
  };

  const prior = incident.feedback || [];

  return (
    <div className="cre-surface rounded-sm p-5 md:p-6" data-testid="reviewer-feedback">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-sm border border-blue-500/40 bg-blue-500/10 flex items-center justify-center">
          <MessageSquare className="h-4 w-4 text-blue-300" strokeWidth={1.5} />
        </div>
        <div>
          <div className="cre-overline text-blue-300">Reviewer feedback</div>
          <h3 className="font-heading text-lg font-bold tracking-tight">
            Capture insights without auto re-running
          </h3>
        </div>
      </div>

      <p className="mt-3 text-xs text-zinc-400 leading-relaxed">
        Feedback is appended to the audit transcript. Re-analysis only runs
        when you explicitly click "Re-run with feedback". Advisory only — human validation required.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="cre-overline">Feedback type</Label>
          <Select value={feedbackType} onValueChange={setFeedbackType}>
            <SelectTrigger
              data-testid="feedback-type"
              className="mt-1.5 bg-black/40 border-white/10"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FEEDBACK_TYPES.map(([v, l]) => (
                <SelectItem key={v} value={v}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="cre-overline">Rating (optional)</Label>
          <Select value={rating} onValueChange={setRating}>
            <SelectTrigger
              data-testid="feedback-rating"
              className="mt-1.5 bg-black/40 border-white/10"
            >
              <SelectValue placeholder="No rating" />
            </SelectTrigger>
            <SelectContent>
              {RATINGS.map(([v, l]) => (
                <SelectItem key={v} value={v}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-3">
        <Label className="cre-overline">Comment</Label>
        <Textarea
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="What was on point or off? What did you spot in evidence?"
          className="mt-1.5 bg-black/40 border-white/10 text-sm"
          data-testid="feedback-comment"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          disabled={busy}
          onClick={() => submit(false)}
          data-testid="feedback-save"
          className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white font-semibold"
        >
          <Save className="h-4 w-4 mr-2" strokeWidth={1.75} />
          Save feedback
        </Button>
        <Button
          disabled={busy}
          variant="outline"
          onClick={() => submit(true)}
          data-testid="feedback-rerun"
          className="rounded-sm border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
        >
          <RefreshCcw className="h-4 w-4 mr-2" strokeWidth={1.75} />
          Re-run analysis with feedback
        </Button>
      </div>

      {prior.length ? (
        <div className="mt-5 border-t border-white/5 pt-4">
          <div className="cre-overline mb-2">
            Prior feedback ({prior.length})
          </div>
          <div className="space-y-2">
            {prior.slice().reverse().map((f, i) => (
              <div
                key={f.feedback_id || i}
                className="border border-white/10 rounded-sm p-3 text-xs"
                data-testid={`prior-feedback-${i}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <span className="font-mono text-zinc-300">
                    {f.feedback_type}
                  </span>
                  <span className="font-mono text-zinc-500">
                    {f.created_at}
                  </span>
                </div>
                {f.rating ? (
                  <div className="mt-1 inline-flex items-center gap-1 text-emerald-300 font-mono text-[11px]">
                    <ThumbsUp className="h-3 w-3" strokeWidth={1.75} />
                    {f.rating.replace("_", " ")}
                  </div>
                ) : null}
                <p className="mt-1 text-zinc-300">{f.comment}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
